import React, { useCallback, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { InputGroup, Input, InputGroupAddon, InputGroupText, Button } from 'reactstrap';

const DurationCustomComponent = (props) => {
    const { input, sign, onDurationChange, onlyParts, setCustomFieldIsVisible, noGoBack } = props;
    const { t }     = useTranslation();

    const normalizer = useCallback(value => {
        if (!value)
            return value;

        value = value.toString().replace(/([+-])/g, '');

        if (sign === '-') {
            value = '-' + value;
        }

        return value;
    }, [sign]);

    useEffect(() => {
        const normalized = normalizer(input.value);
        if (normalized !== input.value)
            input.onChange(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sign]);

    const extractValue = key => {
        // Since M is used for months and for minutes, we use X for minutes...
        const regexp = key === 'X' ? new RegExp('[TH](\\d+)M') : (
            key === 'M' ? new RegExp('[PY](\\d+)M') : new RegExp(`(\\d+)${key}`)
        );
        const results = regexp.exec(input?.value);
        if (results && results.length > 1)
            return parseInt(results[1], 10);

        return 0;
    };

    const formatDuration = (values) => {
        const date = [
            values.Y > 0 ? `${values.Y}Y` : '',
            values.M > 0 ? `${values.M}M` : '',
            values.D > 0 ? `${values.D}D` : ''
        ].join('');
        const time = [
            values.H > 0 ? `${values.H}H` : '',
            values.X > 0 ? `${values.X}M` : '',
            values.S > 0 ? `${values.S}S` : ''
        ].join('');
        const result = `P${date}${time ? `T${time}` : ''}`;

        return result === 'P' ? 'PT0M' : result;
    };

    const onChange = (name, value) => {
        const numericValue = Math.max(0, parseInt(value, 10) || 0);
        const values = {
            Y: extractValue('Y'),
            M: extractValue('M'),
            D: extractValue('D'),
            H: extractValue('H'),
            X: extractValue('X'),
            S: extractValue('S'),
        };
        values[name] = numericValue;

        const normalizedDuration = normalizer(formatDuration(values));
        input?.onChange && input.onChange(normalizedDuration);
        onDurationChange && onDurationChange(normalizedDuration);
    }

    const parts = [
        { key: 'Y', label: 'years', abbr: 'years_abbr' },
        { key: 'M', label: 'months', abbr: 'months_abbr' },
        { key: 'D', label: 'days', abbr: 'days_abbr' },
        { key: 'H', label: 'hours', abbr: 'hours_abbr' },
        // Since M is used for months and for minutes, we use X for minutes...
        { key: 'X', label: 'minutes', abbr: 'minutes_abbr' },
        { key: 'S', label: 'seconds', abbr: 'seconds_abbr' },
    ].filter(part => !onlyParts || onlyParts.includes(part.key));

    const backToPredefinedField = () => {
        input?.onChange && input.onChange(null);
        setCustomFieldIsVisible(false);
    };

    const durationPartLabel = (part) => {
        if (part.key === 'X') {
            const c = Math.max(0, extractValue('X'));
            return t('common.duration.minutes', { count: c });
        }
        const key = onlyParts?.length ? part.label : part.abbr;
        return t(`common.duration.${key}`);
    };

    return (
        <InputGroup>
            <InputGroupAddon addonType="prepend">
                {noGoBack ? (
                <Button>
                    <i className="fa fa-calendar"></i>
                </Button>
                ) : (
                <Button onClick={ backToPredefinedField }>
                    <i className="fa fa-arrow-left"></i>
                </Button>
                )}
            </InputGroupAddon>
            {parts.map(part => (
                <Fragment key={ part.key }>
                    <Input value={ extractValue(part.key) } type="number" step={ 1 } size={ 2 } onChange={ e => onChange(part.key, e.target.value) } />
                    <InputGroupAddon addonType="append">
                        <InputGroupText title={ durationPartLabel(part) }>{ durationPartLabel(part) }</InputGroupText>
                    </InputGroupAddon>
                </Fragment>
            ))}
        </InputGroup>
    );
}

export default DurationCustomComponent;
